import { useState } from "react";
import { toast } from "sonner";
import {
  Users2, Plus, ArrowLeft, Building2, FileText, UserPlus, Crown, LogOut,
  Layers, PlusCircle, Share2, Copy, Sparkles,
} from "lucide-react";
import {
  Button, Card, CardBody, CardHeader, Badge, PageHeader, EmptyState, Loading,
  AccessDenied, Alert, Field, TextInput, Textarea, Select, Modal, ConfirmDialog, Tabs,
} from "@/components/redvolt/kit";
import {
  GROUP_STATUS, PART_STATUS, LEVEL_LABEL, fmtDate, useLoader, apiError,
} from "@/app/federation/common";

const GBadge = ({ status }) => {
  const s = GROUP_STATUS[status] || { label: status, variant: "neutral" };
  return <Badge variant={s.variant}>{s.label}</Badge>;
};
const PBadge = ({ status }) => {
  const s = PART_STATUS[status] || { label: status, variant: "neutral" };
  return <Badge variant={s.variant}>{s.label}</Badge>;
};

/* ------------------------------ Create group ---------------------------- */
const CreateGroupModal = ({ api, tenantId, onClose, onDone }) => {
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (name.trim().length < 2) return;
    setBusy(true);
    try {
      await api.post(`/tenants/${tenantId}/coordination/groups`, { name: name.trim(), purpose: purpose.trim() });
      toast.success("Coordination effort created");
      onDone(); onClose();
    } catch (e) { apiError(e, "Could not create"); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} testid="create-group-modal" title="Start a coordination effort"
      description="Coordination lets several organizations work together — without changing anyone’s structure."
      footer={<>
        <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        <Button size="sm" onClick={submit} disabled={busy || name.trim().length < 2} data-testid="submit-group">{busy ? "Creating…" : "Create"}</Button>
      </>}>
      <div className="space-y-4">
        <Field label="Name" htmlFor="grp-name"><TextInput id="grp-name" data-testid="group-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. State Senate District 9 Coordination" /></Field>
        <Field label="Purpose (optional)" htmlFor="grp-purpose"><TextInput id="grp-purpose" value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="What is this effort about?" /></Field>
      </div>
    </Modal>
  );
};

/* ------------------------------ Invite ---------------------------------- */
const InviteModal = ({ api, tenantId, gid, onClose, onDone }) => {
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!target.trim()) return;
    setBusy(true);
    try {
      await api.post(`/tenants/${tenantId}/coordination/groups/${gid}/participants`, { target_tenant_id: target.trim() });
      toast.success("Invitation sent");
      onDone(); onClose();
    } catch (e) { apiError(e, "Could not invite"); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} testid="invite-modal" title="Invite an organization"
      footer={<>
        <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        <Button size="sm" onClick={submit} disabled={busy || !target.trim()} data-testid="submit-invite">{busy ? "Sending…" : "Send invite"}</Button>
      </>}>
      <Field label="Organization ID" htmlFor="inv-target" hint="Participation lets organizations collaborate; it does not grant access to their data on its own.">
        <TextInput id="inv-target" data-testid="invite-target-input" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="Paste the organization's ID" />
      </Field>
    </Modal>
  );
};

/* ------------------------------ Contribute ------------------------------ */
const ContributeModal = ({ api, tenantId, gid, categories, onClose, onDone }) => {
  const [kind, setKind] = useState("contribute");
  const [title, setTitle] = useState("");
  const [cat, setCat] = useState(categories[0]?.key || "resource");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      const path = kind === "native" ? "native" : "contribute";
      await api.post(`/tenants/${tenantId}/coordination/groups/${gid}/resources/${path}`, {
        title: title.trim(), category_key: cat, body: body.trim(),
      });
      toast.success("Added to the workspace");
      onDone(); onClose();
    } catch (e) { apiError(e, "Could not add"); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} testid="contribute-modal" title="Add to the shared workspace"
      footer={<>
        <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        <Button size="sm" onClick={submit} disabled={busy || !title.trim()} data-testid="submit-contribute">{busy ? "Adding…" : "Add"}</Button>
      </>}>
      <div className="space-y-4">
        <Field label="Type" htmlFor="c-kind" hint={kind === "native" ? "Created here, owned by the coordination effort." : "A copy contributed from your organization (provenance is kept)."}>
          <Select id="c-kind" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="contribute-kind-select">
            <option value="contribute">Contribute a copy from my organization</option>
            <option value="native">Create new in this coordination</option>
          </Select>
        </Field>
        <Field label="Title" htmlFor="c-title"><TextInput id="c-title" data-testid="contribute-title-input" value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
        <Field label="Category" htmlFor="c-cat"><Select id="c-cat" value={cat} onChange={(e) => setCat(e.target.value)}>{categories.map((c) => <option key={c.key} value={c.key}>{c.display}</option>)}</Select></Field>
        <Field label="Notes (optional)" htmlFor="c-body"><Textarea id="c-body" rows={3} value={body} onChange={(e) => setBody(e.target.value)} /></Field>
      </div>
    </Modal>
  );
};

/* ------------------------------ Assign rep ------------------------------ */
const RepModal = ({ api, tenantId, gid, onClose, onDone }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post(`/tenants/${tenantId}/coordination/groups/${gid}/representatives`, { user_email: email.trim() });
      toast.success("Representative assigned");
      onDone(); onClose();
    } catch (e) { apiError(e, "Could not assign"); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} testid="rep-modal" title="Assign a representative"
      description="Choose a member of your organization to act on its behalf in this effort."
      footer={<>
        <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        <Button size="sm" onClick={submit} disabled={busy || !email.trim()} data-testid="submit-rep">{busy ? "Assigning…" : "Assign"}</Button>
      </>}>
      <Field label="Member email" htmlFor="rep-email"><TextInput id="rep-email" data-testid="rep-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="member@yourorg.com" /></Field>
    </Modal>
  );
};

/* ------------------------------ Group detail ---------------------------- */
const GroupDetail = ({ api, active, caps, gid, onBack }) => {
  const tenantId = active.tenant_id;
  const [tab, setTab] = useState("participants");
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const groupQ = useLoader(async () => (await api.get(`/tenants/${tenantId}/coordination/groups/${gid}`)).data, [gid]);
  const resQ = useLoader(async () => (await api.get(`/tenants/${tenantId}/coordination/groups/${gid}/resources`)).data.resources, [gid]);
  const catsQ = useLoader(async () => (await api.get(`/reference/sharing-categories`)).data.categories, []);
  const g = groupQ.data;

  const canCoordinate = caps.includes("coordination:coordinate");
  const canManageGroup = caps.includes("coordination:manage_group");
  const canParticipate = caps.includes("coordination:manage_participation");
  const canContribute = caps.includes("coordination:contribute");

  const post = async (path, ok, reload = true) => {
    setBusy(true);
    try {
      await api.post(`/tenants/${tenantId}/coordination/groups/${gid}/${path}`);
      toast.success(ok);
      if (reload) { groupQ.reload(); }
    } catch (e) { apiError(e); }
    finally { setBusy(false); setConfirm(null); }
  };
  const postBody = async (path, body, ok) => {
    setBusy(true);
    try { await api.post(`/tenants/${tenantId}/coordination/groups/${gid}/${path}`, body); toast.success(ok); groupQ.reload(); }
    catch (e) { apiError(e); }
    finally { setBusy(false); setConfirm(null); }
  };

  if (groupQ.loading) return <Loading label="Loading coordination effort…" />;
  if (groupQ.denied || !g) return <AccessDenied message="You don’t have access to this coordination effort." />;

  const participants = g.participants || [];
  const iAmActive = g.my_status === "active";
  const iAmInvited = g.my_status === "invited";
  const operational = g.status === "active";

  const tabs = [
    { key: "participants", label: "Participants", count: participants.length },
    { key: "resources", label: "Workspace", count: (resQ.data || []).length },
    ...(canManageGroup ? [{ key: "governance", label: "Governance" }] : []),
  ];

  return (
    <div className="space-y-6" data-testid="group-detail">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800" data-testid="back-to-groups">
        <ArrowLeft className="h-4 w-4" /> All coordination
      </button>

      {/* Representation banner */}
      {iAmActive && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/20 bg-accent px-4 py-3 text-sm" data-testid="representation-banner">
          <Sparkles className="h-4 w-4 text-primary" />
          <span className="text-slate-700">Representing: <span className="font-semibold text-slate-900">{active.tenant_name}</span></span>
          <span className="text-slate-400">·</span>
          <span className="text-slate-700">Working in: <span className="font-semibold text-slate-900">{g.name}</span></span>
        </div>
      )}

      <PageHeader title={g.name} subtitle={g.purpose || "Coordination effort"} action={<GBadge status={g.status} />} />

      {iAmInvited && canParticipate && (
        <Alert variant="info" title="You’ve been invited">
          <div className="mt-2 flex gap-2">
            <Button size="sm" onClick={() => post("accept", "Joined the effort")} data-testid="accept-participation">Accept invitation</Button>
            <Button size="sm" variant="outline" onClick={() => post("decline", "Invitation declined")} data-testid="decline-participation">Decline</Button>
          </div>
        </Alert>
      )}

      {!operational && (
        <Alert variant={g.status === "completed" ? "info" : "warning"}>
          {g.status === "completed" ? "This effort is completed. Live cross-organization sharing through it has ended." : `This effort is ${GROUP_STATUS[g.status]?.label?.toLowerCase() || g.status}.`}
        </Alert>
      )}

      <Tabs tabs={tabs} value={tab} onChange={setTab} />

      {tab === "participants" && (
        <Card><CardHeader title="Participating organizations" icon={Building2}
          action={<div className="flex gap-2">
            {canCoordinate && operational && <Button size="sm" variant="outline" onClick={() => setModal("invite")} data-testid="invite-button"><UserPlus className="h-4 w-4" /> Invite</Button>}
            {iAmActive && canParticipate && <Button size="sm" variant="outline" onClick={() => setConfirm({ type: "withdraw" })} data-testid="withdraw-button"><LogOut className="h-4 w-4" /> Withdraw</Button>}
          </div>} />
          <CardBody className="pt-4">
            {participants.length === 0 ? (
              <EmptyState icon={Building2} title="No participants yet" description="Invite organizations to collaborate in this effort." />
            ) : (
              <div className="space-y-2">
                {participants.map((p) => (
                  <div key={p.tenant_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 px-4 py-3" data-testid={`participant-${p.tenant_id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="grid h-8 w-8 place-items-center rounded-md bg-accent text-primary"><Building2 className="h-4 w-4" /></span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-900">{p.org_name || "Organization"}</p>
                        <p className="text-xs text-slate-500 capitalize">{p.coordination_role}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {p.coordination_role === "coordinator" && <Badge variant="info"><Crown className="h-3 w-3" /> Coordinator</Badge>}
                      <PBadge status={p.status} />
                      {canCoordinate && operational && p.status === "active" && p.tenant_id !== tenantId && (
                        <>
                          {p.coordination_role !== "coordinator" && <Button size="sm" variant="ghost" onClick={() => postBody("participants/promote", { target_tenant_id: p.tenant_id }, "Promoted to coordinator")} data-testid={`promote-${p.tenant_id}`}>Make coordinator</Button>}
                          <Button size="sm" variant="destructive" onClick={() => setConfirm({ type: "remove", target: p.tenant_id })} data-testid={`remove-${p.tenant_id}`}>Remove</Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {iAmActive && canParticipate && (
              <div className="mt-4 border-t border-slate-100 pt-4">
                <Button size="sm" variant="outline" onClick={() => setModal("rep")} data-testid="assign-rep-button"><UserPlus className="h-4 w-4" /> Assign a representative</Button>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {tab === "resources" && (
        <Card><CardHeader title="Shared workspace" icon={Layers}
          action={canContribute && iAmActive && operational && <Button size="sm" variant="outline" onClick={() => setModal("contribute")} data-testid="contribute-button"><PlusCircle className="h-4 w-4" /> Add</Button>} />
          <CardBody className="pt-4">
            {resQ.loading ? <Loading label="Loading workspace…" /> : (resQ.data || []).length === 0 ? (
              <EmptyState icon={FileText} title="No shared resources yet" description="Organizations choose what to contribute. Contributed items keep a record of where they came from." />
            ) : (
              <div className="space-y-2" data-testid="workspace-resources">
                {resQ.data.map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-4 py-3" data-testid={`workspace-resource-${r.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="grid h-8 w-8 place-items-center rounded-md bg-accent text-primary">{r.resource_kind === "native" ? <FileText className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-900">{r.title}</p>
                        <p className="text-xs text-slate-500">{r.resource_kind === "native" ? "Created in this coordination" : "Contributed copy"}{r.status !== "active" ? ` · ${r.status}` : ""}</p>
                      </div>
                    </div>
                    <Badge variant={r.resource_kind === "native" ? "info" : "neutral"}>{r.category}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {tab === "governance" && canManageGroup && (
        <Card><CardHeader title="Effort governance" icon={Crown} />
          <CardBody className="pt-4 space-y-3">
            <p className="text-sm text-slate-500">Manage the lifecycle of this coordination effort. These actions affect every participant.</p>
            <div className="flex flex-wrap gap-2">
              {g.status === "active" && <Button size="sm" variant="outline" onClick={() => post("pause", "Effort paused")} data-testid="pause-group">Pause</Button>}
              {g.status === "paused" && <Button size="sm" variant="outline" onClick={() => post("resume", "Effort resumed")} data-testid="resume-group">Resume</Button>}
              {["active", "paused"].includes(g.status) && <Button size="sm" variant="outline" onClick={() => setConfirm({ type: "complete" })} data-testid="complete-group">Complete</Button>}
              {g.status === "completed" && <Button size="sm" variant="outline" onClick={() => setConfirm({ type: "reopen" })} data-testid="reopen-group">Reopen</Button>}
              {["active", "paused", "completed"].includes(g.status) && <Button size="sm" variant="destructive" onClick={() => setConfirm({ type: "dissolve" })} data-testid="dissolve-group">Dissolve</Button>}
            </div>
          </CardBody>
        </Card>
      )}

      {modal === "invite" && <InviteModal api={api} tenantId={tenantId} gid={gid} onClose={() => setModal(null)} onDone={groupQ.reload} />}
      {modal === "contribute" && <ContributeModal api={api} tenantId={tenantId} gid={gid} categories={catsQ.data || []} onClose={() => setModal(null)} onDone={resQ.reload} />}
      {modal === "rep" && <RepModal api={api} tenantId={tenantId} gid={gid} onClose={() => setModal(null)} onDone={groupQ.reload} />}

      <ConfirmDialog open={!!confirm} onCancel={() => setConfirm(null)} busy={busy}
        onConfirm={() => {
          if (confirm.type === "withdraw") return post("withdraw", "Withdrew from the effort");
          if (confirm.type === "remove") return postBody("participants/remove", { target_tenant_id: confirm.target }, "Participant removed");
          if (confirm.type === "complete") return post("complete", "Effort completed");
          if (confirm.type === "reopen") return post("reopen", "Effort reopened");
          if (confirm.type === "dissolve") return post("dissolve", "Effort dissolved");
        }}
        title={{
          withdraw: "Withdraw from this effort?", remove: "Remove this participant?",
          complete: "Complete this effort?", reopen: "Reopen this effort?", dissolve: "Dissolve this effort?",
        }[confirm?.type]}
        confirmLabel={{ withdraw: "Withdraw", remove: "Remove", complete: "Complete", reopen: "Reopen", dissolve: "Dissolve" }[confirm?.type]}
        destructive={confirm?.type !== "reopen" && confirm?.type !== "complete"}>
        {confirm?.type === "withdraw" && "Withdrawing ends your organization’s future access to resources shared through this effort. Existing activity history remains."}
        {confirm?.type === "remove" && "This organization will lose future access to sharing through this effort. History is kept."}
        {confirm?.type === "complete" && "Completing ends live cross-organization sharing through this effort. The workspace remains for reference."}
        {confirm?.type === "reopen" && "Reopening does not automatically restore previous sharing — cross-organization access must be set up again."}
        {confirm?.type === "dissolve" && "Dissolving permanently ends this effort and all sharing through it. This cannot be undone."}
      </ConfirmDialog>
    </div>
  );
};

/* ------------------------------- Page ----------------------------------- */
export default function CoordinationPage({ api, active, caps }) {
  const tenantId = active.tenant_id;
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const canView = caps.includes("coordination:view");
  const canManageGroup = caps.includes("coordination:manage_group");

  const q = useLoader(async () => (await api.get(`/tenants/${tenantId}/coordination/groups`)).data.groups, [tenantId]);

  if (!canView) return <AccessDenied message="Your role doesn’t include viewing coordination." />;
  if (selected) return <GroupDetail api={api} active={active} caps={caps} gid={selected} onBack={() => { setSelected(null); q.reload(); }} />;

  const groups = q.data || [];
  return (
    <div className="space-y-6" data-testid="coordination-page">
      <PageHeader title="Coordination"
        subtitle="Work together with other organizations — without changing anyone’s structure."
        action={canManageGroup && <Button onClick={() => setShowCreate(true)} data-testid="create-group-button"><Plus className="h-4 w-4" /> Start an effort</Button>} />
      {q.loading ? <Loading label="Loading coordination…" /> : groups.length === 0 ? (
        <EmptyState icon={Users2} title="Coordinate across organizational lines"
          description="Create a shared effort for a legislative district, candidate, special election, joint event, or another project involving multiple organizations — without changing anyone's organizational structure."
          action={canManageGroup && <Button size="sm" onClick={() => setShowCreate(true)}>Start an effort</Button>} />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {groups.map((grp) => (
            <button key={grp.id} onClick={() => setSelected(grp.id)} data-testid={`group-${grp.id}`}
              className="rounded-lg border border-slate-200 bg-white p-4 text-left transition-colors hover:border-primary/40 hover:bg-slate-50">
              <div className="flex items-start justify-between gap-2">
                <span className="grid h-9 w-9 place-items-center rounded-md bg-accent text-primary"><Users2 className="h-4 w-4" /></span>
                <GBadge status={grp.status} />
              </div>
              <p className="mt-3 text-sm font-semibold text-slate-900">{grp.name}</p>
              <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">{grp.purpose || "Coordination effort"}</p>
            </button>
          ))}
        </div>
      )}
      {showCreate && <CreateGroupModal api={api} tenantId={tenantId} onClose={() => setShowCreate(false)} onDone={q.reload} />}
    </div>
  );
}
